import { useEffect, useId, useRef, type ReactNode } from 'react'
import { cn } from '@/shared/utils/cn'

export interface DialogProps {
  open: boolean
  /** Fired for every way out: Esc, the close() call, a cancel action. */
  onClose: () => void
  title: string
  description?: string
  /**
   * Anything the question needs answering with — a reason dropdown, say — between
   * the description and the actions. A separate slot from `children` because the
   * action row stretches its children to equal widths, which would make a control
   * put there share the row with the buttons.
   */
  body?: ReactNode
  /** The action row — typically a secondary "go back" and a primary confirm. */
  children?: ReactNode
  className?: string
}

/**
 * A modal confirmation dialog on the native `<dialog>` element, which supplies
 * focus trapping, Esc handling, top-layer stacking and background inertness for
 * free — behaviours that hand-rolled overlays reliably get wrong.
 *
 * `role="alertdialog"` because every use in this app interrupts to ask for a
 * decision; there is deliberately no backdrop-click dismissal, so an accidental
 * tap cannot answer the question for the citizen.
 *
 * Dressed from board 2034:20705, the redesign's one confirm dialog: a 400px
 * 16px-radius card at 32px padding, its question and consequence CENTRED and
 * 12px apart, then 24px down a row of two equal-width 48px buttons. Centring is
 * what distinguishes this from a panel — the reader has one thing to decide and
 * the layout gives them nothing else to scan. The two buttons split the row
 * evenly rather than huddling right, so neither answer is the easier target.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  body,
  children,
  className,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  // Generated, not the literal ids this used to carry: two dialogs mounted at
  // once would otherwise both claim `dialog-title`, and every one of them would
  // be labelled by whichever the document happened to reach first.
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      // Fallback: environments without showModal (jsdom, pre-2022 browsers) get a
      // non-modal dialog — visible and usable, just without trap/backdrop.
      if (typeof dialog.showModal === 'function') {
        dialog.showModal()
      } else {
        dialog.setAttribute('open', '')
      }
    } else if (!open && dialog.open) {
      // Symmetrical with the fallback above. An environment that lacks showModal
      // lacks close() too, and calling it there throws out of an effect — which
      // takes the whole tree to the error boundary the first time a citizen
      // backs out of a confirmation.
      if (typeof dialog.close === 'function') {
        dialog.close()
      } else {
        dialog.removeAttribute('open')
      }
    }
  }, [open])

  /**
   * Close on unmount, separately from the `open` effect above, which never gets the
   * chance to run when the dialog's OWNER disappears in the same commit.
   *
   * That is the normal case here, not an edge one: ResetApplicationButton returns
   * null the moment the session is cleared, and the confirm handler clears it. A
   * modal `<dialog>` removed from the DOM while still open takes the top layer with
   * it — keyboard focus and the screen reader are dropped to `<body>` with nothing
   * announced, and the backdrop can outlive the element.
   *
   * Mount-scoped, so it runs only on unmount, and it reuses the same capability check
   * as above: an environment without showModal has no close() either, and throwing
   * from a cleanup takes the whole tree to the error boundary.
   */
  useEffect(
    () => () => {
      const dialog = ref.current
      if (dialog?.open && typeof dialog.close === 'function') dialog.close()
    },
    []
  )

  return (
    <dialog
      ref={ref}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClose={onClose}
      onCancel={onClose}
      className={cn(
        'm-auto w-full max-w-[25rem] rounded-xl border border-hairline bg-card p-4',
        'shadow-dialog backdrop:bg-scrim',
        className
      )}
    >
      <div className="flex flex-col items-center gap-1.5 text-center">
        <h2 id={titleId} className="text-h5 text-ink-900">
          {title}
        </h2>
        {description && (
          <p id={descriptionId} className="text-body2 text-ink-700">
            {description}
          </p>
        )}
      </div>
      {/* Left-aligned, unlike the question above it: a labelled control reads as
          a form field, and a centred one loses the label-to-input relationship
          the rest of the app draws. */}
      {body && <div className="mt-2.5 text-left">{body}</div>}
      {/* Each action gets an equal share of the row. `flex-1` here rather than on
          every call site: the split is the dialog's layout, not the caller's. */}
      {children && <div className="mt-3 flex gap-1.5 *:flex-1">{children}</div>}
    </dialog>
  )
}
